import { FC, useEffect } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import LogoutIcon from '@/assets/svg/icon-logout.svg?react';
import IconUser from '@/assets/svg/icon-user.svg?react';
import MainLogo from '@/assets/svg/main-logo-flat.svg?react';
import BackIcon from '@/assets/svg/nav-back.svg?react';
import MenuIcon from '@/assets/svg/nav-hamburger.svg?react';

import { cn } from '@/lib/utils';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarProvider,
  useSidebar,
} from '@/components/ui/sidebar';
import { BackButton } from '@/components/BackButton/BackButton';

import { useAuthStore } from '@/stores/authStore';

import { USER_ROLES } from '@/constants/global-constants';
import {
  getPageTitle,
  publicRoutes,
  RouteNameValues,
  ROUTES,
} from '@/constants/route-settings';

const SidebarToggle = () => {
  const { toggleSidebar } = useSidebar();

  return (
    <button
      className="fixed left-5 top-5 z-50 md:hidden"
      onClick={toggleSidebar}
    >
      <MenuIcon className="h-6 w-6" />
    </button>
  );
};

const SidebarToggleOff = () => {
  const { toggleSidebar } = useSidebar();
  return (
    <button onClick={toggleSidebar}>
      <BackIcon className="h-6 w-6" />
    </button>
  );
};

const Navigation = () => {
  const { toggleSidebar } = useSidebar();
  const { signOut, user } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <Sidebar className="w-[300px] bg-sidebar p-2 text-sidebar-foreground">
      <SidebarHeader className="flex px-2 py-4">
        <div className="flex items-center gap-4 px-4">
          <SidebarToggleOff />
          <MainLogo className="ml-2 h-[34px] w-[180px] fill-[#F58426] md:w-[200px]" />
        </div>
      </SidebarHeader>
      <SidebarContent className="p-2">
        <SidebarMenu>
          {user &&
            Object.values(ROUTES[user.role])
              .filter((route) => route.isNav)
              .map(({ path, title, pathsInclude, icon: Icon }) => (
                <SidebarMenuItem key={title}>
                  <NavLink
                    to={path}
                    onClick={toggleSidebar}
                    className={({ isActive }) =>
                      cn(
                        'flex w-full cursor-pointer items-center gap-3 rounded-md p-4 transition-colors',
                        isActive ||
                          pathsInclude?.some((pathInclude) =>
                            location.pathname.includes(pathInclude),
                          )
                          ? 'bg-sidebar-hover'
                          : 'hover:bg-sidebar-hover',
                      )
                    }
                  >
                    {!!Icon && (
                      <div className="relative h-[30px] min-w-[30px]">
                        <Icon className="absolute left-1/2 top-1/2 h-[30px] min-w-5 -translate-x-1/2 -translate-y-1/2 fill-white" />
                      </div>
                    )}
                    <span>{title}</span>
                  </NavLink>
                </SidebarMenuItem>
              ))}
        </SidebarMenu>
      </SidebarContent>
      <SidebarFooter>
        <div className="flex w-full items-center justify-between p-2">
          <div className="flex items-center gap-4">
            <div className="relative h-[30px] min-w-[30px]">
              <IconUser className="absolute left-1/2 top-1/2 h-[30px] min-w-5 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-semibold leading-5">
                {user?.username}
              </span>
              <span className="text-sm font-medium leading-none">
                {user &&
                  Object.values(USER_ROLES).find(
                    (role) => role.value === user.role,
                  )?.title}
              </span>
            </div>
          </div>
          <button
            className="flex cursor-pointer items-center rounded-md p-2 transition-colors hover:bg-sidebar-hover"
            onClick={() => {
              signOut();
              navigate(publicRoutes.login);
            }}
          >
            <LogoutIcon className="h-[20px] w-[26px] fill-white" />
          </button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
};

export const LayoutMobileWidget: FC = () => {
  const { user, token } = useAuthStore();
  const location = useLocation();
  const isSettingsPage = user !== null && user.role === USER_ROLES.ADMIN.value;

  // Add effect to handle viewport height for mobile Safari
  useEffect(() => {
    // First, set the value in CSS
    const setCorrectVh = () => {
      // Get the viewport height and multiply by 1% to get a value for 1vh unit
      const vh = window.innerHeight * 0.01;
      // Set the value in the --vh custom property to the root of the document
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set the height initially
    setCorrectVh();

    // Update the height on resize and orientation change
    window.addEventListener('resize', setCorrectVh);
    window.addEventListener('orientationchange', setCorrectVh);

    // Clean up
    return () => {
      window.removeEventListener('resize', setCorrectVh);
      window.removeEventListener('orientationchange', setCorrectVh);
    };
  }, []);

  return (
    <SidebarProvider defaultOpen>
      <div className="min-h-real-screen flex flex-1">
        {!!token && !!user && (
          <>
            <SidebarToggle />
            <Navigation />
          </>
        )}
        <main className="flex flex-1 flex-col bg-background">
          <nav
            className={cn(
              isSettingsPage ? '' : 'border-b',
              'sticky top-0 z-20 flex h-[50px] items-center bg-white px-4 sm:h-[60px] md:h-[70px] md:pl-[50px]',
            )}
          >
            <div className="ml-12 flex items-center md:ml-0">
              <BackButton />
              <h1 className="text-xl font-semibold text-nav-text">
                {user !== null &&
                  getPageTitle(location.pathname as RouteNameValues, user.role)}
              </h1>
            </div>
          </nav>

          <div className="flex flex-1 flex-col overflow-y-auto bg-page-background">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};
